import {
  DASHBOARD_API_BASE_URL,
  type DashboardApiResponse,
} from '../config/config'
import { ADMIN_TOKEN_KEY } from '../../auth/adminAuth'
import type {
  PaymentRecord,
  PaymentMethodKind,
  PaymentTxnStatus,
} from '../../types'

export type PaymentSummary = {
  totalRevenue: number
  todayRevenue: number
  pendingAmount: number
  todayPaymentsCount: number
  pendingPaymentsCount: number
  revenueChangePercent: number | null
}

type ApiPayment = {
  id: number
  payment_reference: string
  booking_id: string
  customer: PaymentRecord['customer']
  service_type: string
  pickup_location: string
  drop_location: string
  vehicle_name: string
  driver_name: string
  payment_method: string
  provider: string
  amount: string | number
  status: 'succeeded' | 'pending' | 'partial' | 'failed' | 'refunded'
  paid_at: string | null
  created_at: string
}

type ApiPage = {
  count: number
  next: string | null
  results: ApiPayment[]
}

const numberValue = (
  value: unknown,
  field: string,
): number => {
  if (
    (typeof value !== 'number' && typeof value !== 'string') ||
    (typeof value === 'string' && !value.trim()) ||
    !Number.isFinite(Number(value))
  ) {
    throw new Error(`Invalid payment field: ${field}`)
  }

  return Number(value)
}

const request = async <T,>(
  path: string,
  signal?: AbortSignal,
): Promise<T> => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY)

  if (!token || token === 'demo_admin_token_active') {
    throw new Error('Please sign in with a real admin account.')
  }

  const response = await fetch(
    `${DASHBOARD_API_BASE_URL.replace(/\/+$/, '')}${path}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal,
    },
  )

  const body = await response.json()

  if (
    !body ||
    typeof body !== 'object' ||
    Array.isArray(body)
  ) {
    throw new Error('Invalid payments API response.')
  }

  if (!response.ok || body.success === false) {
    throw new Error(
      typeof body.message === 'string'
        ? body.message
        : typeof body.detail === 'string'
          ? body.detail
          : `Payments request failed (HTTP ${response.status}).`,
    )
  }

  return body as T
}

const toPayment = (row: ApiPayment): PaymentRecord => {
  const strings: (keyof ApiPayment)[] = [
    'payment_reference',
    'booking_id',
    'service_type',
    'pickup_location',
    'drop_location',
    'vehicle_name',
    'driver_name',
    'payment_method',
    'provider',
  ]

  if (
    !row ||
    !Number.isSafeInteger(row.id) ||
    row.id < 1 ||
    strings.some((field) => typeof row[field] !== 'string') ||
    !row.customer ||
    typeof row.customer.name !== 'string' ||
    typeof row.customer.email !== 'string' ||
    typeof row.customer.phone !== 'string'
  ) {
    throw new Error('Invalid payment record response.')
  }

const statuses: Record<ApiPayment['status'], PaymentTxnStatus> = {
  succeeded: 'paid',
  pending: 'pending',
  partial: 'pending',
  failed: 'failed',
  refunded: 'refunded',
}

  const status = statuses[row.status]

  if (!status) {
    throw new Error(
    `Unknown backend payment status: ${String(row.status)}`
  )
  }

  const method: PaymentMethodKind =
    row.payment_method === 'paypal'
      ? 'paypal'
      : ['card', 'credit-card'].includes(row.payment_method)
        ? 'credit-card'
        : row.payment_method === 'cash'
          ? 'cash'
          : 'other'

  const labels: Record<string, string> = {
    cash: 'Cash',
    card: 'Credit Card',
    'credit-card': 'Credit Card',
    paypal: 'PayPal',
    online: 'Online',
  }

  const services: Record<string, string> = {
    daytrips: 'Day Trips',
    privatetours: 'Private Tours',
    skitransfers: 'Ski Transfers',
  }

  const timestamp = row.paid_at || row.created_at

  if (
    !timestamp ||
    !Number.isFinite(Date.parse(timestamp))
  ) {
    throw new Error('Invalid backend payment date.')
  }

  return {
    id: String(row.id),
    transactionId: row.payment_reference,
    invoice: '�',
    customer: row.customer,
    bookingId: row.booking_id,
    service: services[row.service_type] || row.service_type,
    route: {
      from: row.pickup_location,
      to: row.drop_location,
    },
    routeLabel: `${row.pickup_location} ? ${row.drop_location}`,
    vehicle: row.vehicle_name || '�',
    driver: row.driver_name || '�',
    amountEur: numberValue(row.amount, 'amount'),
    subtotalEur: null,
    taxesEur: null,
    discountEur: null,
    method,
    methodLabel:
      labels[row.payment_method] || row.payment_method || '�',
    gateway: row.provider,
    status,
    paidAt: timestamp,
  }
}

export const apiGetPayments = async (
  signal?: AbortSignal,
): Promise<DashboardApiResponse<PaymentRecord[]>> => {
  try {
    const records: PaymentRecord[] = []
    let pageNumber = 1

    while (true) {
      const page = await request<ApiPage>(
        `/api/admin/payments/?page=${pageNumber}`,
        signal,
      )

      if (
        !Array.isArray(page.results) ||
        !Number.isSafeInteger(page.count) ||
        page.count < 0 ||
        (page.next !== null && typeof page.next !== 'string')
      ) {
        throw new Error('Invalid payments pagination response.')
      }

      records.push(...page.results.map(toPayment))

      if (!page.next) break

      if (
        !page.results.length ||
        records.length >= page.count
      ) {
        throw new Error(
          'Inconsistent payments pagination. Please refresh.',
        )
      }

      pageNumber += 1
    }

    const data = Array.from(
      new Map(
        records.map((payment) => [payment.id, payment]),
      ).values(),
    )

    return {
      success: true,
      message: 'Payments loaded.',
      data,
    }
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : 'Payments request failed.',
    }
  }
}

export const apiGetPaymentSummary = async (
  signal?: AbortSignal,
): Promise<DashboardApiResponse<PaymentSummary>> => {
  try {
    const body = await request<{
      data: Record<string, unknown>
    }>(
      '/api/admin/payments/summary/',
      signal,
    )

    const data = body.data

    if (
      !data ||
      typeof data !== 'object' ||
      Array.isArray(data)
    ) {
      throw new Error('Invalid payment summary response.')
    }

    const count = (field: string) => {
      const value = numberValue(data[field], field)

      if (!Number.isSafeInteger(value) || value < 0) {
        throw new Error(`Invalid count: ${field}`)
      }

      return value
    }

    return {
      success: true,
      message: 'Payment summary loaded.',
      data: {
        totalRevenue: numberValue(
          data.total_revenue,
          'total_revenue',
        ),
        todayRevenue: numberValue(
          data.today_revenue,
          'today_revenue',
        ),
        pendingAmount: numberValue(
          data.pending_amount,
          'pending_amount',
        ),
        todayPaymentsCount: count('today_payments_count'),
        pendingPaymentsCount: count('pending_payments_count'),
        revenueChangePercent:
          data.revenue_change_percent === null
            ? null
            : numberValue(
                data.revenue_change_percent,
                'revenue_change_percent',
              ),
      },
    }
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : 'Payment summary request failed.',
    }
  }
}